/**
 * Canvas Editing Layer
 *
 * Provides drag, resize, and select functionality on top of Remotion Player.
 * This is a transparent layer that handles all mouse interactions.
 *
 * Key features:
 * - Click to select overlays
 * - Drag to move overlays
 * - Resize handles for resizing
 * - Snap-to-grid (optional)
 * - Keyboard shortcuts (delete, arrow keys)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { OverlayData, ResizeHandle, Position } from '../types';

interface CanvasEditingLayerProps {
  overlays: OverlayData[];
  containerWidth: number;
  containerHeight: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onUpdate: (id: string, updates: Partial<OverlayData>) => void;
  onDelete?: (id: string) => void;
  snapToGrid?: boolean;
  gridSize?: number;
  scale?: number; // Scale factor for preview (e.g., 0.4 means 40% of original)
}

interface DragState {
  isDragging: boolean;
  startMouseX: number;
  startMouseY: number;
  startOverlayX: number;
  startOverlayY: number;
}

interface ResizeState {
  isResizing: boolean;
  handle: ResizeHandle | null;
  startMouseX: number;
  startMouseY: number;
  startWidth: number;
  startHeight: number;
  startX: number;
  startY: number;
}

export const CanvasEditingLayer: React.FC<CanvasEditingLayerProps> = ({
  overlays,
  containerWidth,
  containerHeight,
  selectedId,
  onSelect,
  onUpdate,
  onDelete,
  snapToGrid = false,
  gridSize = 10,
  scale = 1,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    startMouseX: 0,
    startMouseY: 0,
    startOverlayX: 0,
    startOverlayY: 0,
  });
  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    handle: null,
    startMouseX: 0,
    startMouseY: 0,
    startWidth: 0,
    startHeight: 0,
    startX: 0,
    startY: 0,
  });

  const selectedOverlay = overlays.find((o) => o.id === selectedId);

  // Convert percentage to pixels
  const pctToPx = useCallback(
    (pct: number, dimension: 'x' | 'y') => {
      return dimension === 'x'
        ? (pct / 100) * containerWidth
        : (pct / 100) * containerHeight;
    },
    [containerWidth, containerHeight]
  );

  // Convert pixels to percentage
  const pxToPct = useCallback(
    (px: number, dimension: 'x' | 'y') => {
      return dimension === 'x'
        ? (px / containerWidth) * 100
        : (px / containerHeight) * 100;
    },
    [containerWidth, containerHeight]
  );

  // Snap value to grid
  const snap = useCallback(
    (value: number): number => {
      if (!snapToGrid) return value;
      return Math.round(value / gridSize) * gridSize;
    },
    [snapToGrid, gridSize]
  );

  // Handle click on empty space (deselect)
  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onSelect(null);
      }
    },
    [onSelect]
  );

  // Handle overlay click (select)
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      onSelect(id);
    },
    [onSelect]
  );

  // Start dragging
  const handleDragStart = useCallback(
    (e: React.MouseEvent, overlay: OverlayData) => {
      e.preventDefault();
      e.stopPropagation();

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      setDragState({
        isDragging: true,
        startMouseX: e.clientX - rect.left,
        startMouseY: e.clientY - rect.top,
        startOverlayX: overlay.position.x,
        startOverlayY: overlay.position.y,
      });

      onSelect(overlay.id);
    },
    [onSelect]
  );

  // Start resizing
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: ResizeHandle, measuredHeightPx: number) => {
      e.preventDefault();
      e.stopPropagation();

      if (!selectedOverlay) return;

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Convert measured pixel height to percentage if no explicit height
      const heightPct = selectedOverlay.height !== undefined
        ? selectedOverlay.height
        : pxToPct(measuredHeightPx, 'y');

      setResizeState({
        isResizing: true,
        handle,
        startMouseX: e.clientX - rect.left,
        startMouseY: e.clientY - rect.top,
        startWidth: selectedOverlay.width,
        startHeight: heightPct,
        startX: selectedOverlay.position.x,
        startY: selectedOverlay.position.y,
      });
    },
    [selectedOverlay, pxToPct]
  );

  // Handle mouse move (drag or resize)
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || !selectedId) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      if (dragState.isDragging) {
        // Calculate new position
        const deltaXPx = mouseX - dragState.startMouseX;
        const deltaYPx = mouseY - dragState.startMouseY;

        const newX = snap(dragState.startOverlayX + pxToPct(deltaXPx, 'x'));
        const newY = snap(dragState.startOverlayY + pxToPct(deltaYPx, 'y'));

        // Clamp to container bounds
        const clampedX = Math.max(0, Math.min(100 - (selectedOverlay?.width ?? 10), newX));
        const clampedY = Math.max(0, Math.min(100 - (selectedOverlay?.height ?? 10), newY));

        onUpdate(selectedId, {
          position: { x: clampedX, y: clampedY },
        });
      }

      if (resizeState.isResizing && resizeState.handle) {
        const deltaXPx = mouseX - resizeState.startMouseX;
        const deltaYPx = mouseY - resizeState.startMouseY;
        const deltaXPct = pxToPct(deltaXPx, 'x');
        const deltaYPct = pxToPct(deltaYPx, 'y');

        let newWidth = resizeState.startWidth;
        let newHeight = resizeState.startHeight;
        let newX = resizeState.startX;
        let newY = resizeState.startY;

        // Handle different resize handles
        const handle = resizeState.handle;

        // Horizontal resizing
        if (handle.includes('right')) {
          newWidth = snap(resizeState.startWidth + deltaXPct);
        } else if (handle.includes('left')) {
          newWidth = snap(resizeState.startWidth - deltaXPct);
          newX = snap(resizeState.startX + deltaXPct);
        }

        // Vertical resizing
        if (handle.includes('bottom')) {
          newHeight = snap(resizeState.startHeight + deltaYPct);
        } else if (handle.includes('top')) {
          newHeight = snap(resizeState.startHeight - deltaYPct);
          newY = snap(resizeState.startY + deltaYPct);
        }

        // Clamp dimensions
        newWidth = Math.max(5, Math.min(100, newWidth));
        newHeight = Math.max(5, Math.min(100, newHeight));
        newX = Math.max(0, newX);
        newY = Math.max(0, newY);

        onUpdate(selectedId, {
          width: newWidth,
          height: newHeight,
          position: { x: newX, y: newY },
        });
      }
    },
    [
      dragState,
      resizeState,
      selectedId,
      selectedOverlay,
      pxToPct,
      snap,
      onUpdate,
    ]
  );

  // Handle mouse up (end drag/resize)
  const handleMouseUp = useCallback(() => {
    setDragState((prev) => ({ ...prev, isDragging: false }));
    setResizeState((prev) => ({ ...prev, isResizing: false, handle: null }));
  }, []);

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!selectedId || !selectedOverlay) return;

      const moveAmount = e.shiftKey ? 10 : 1; // Shift for larger moves

      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          if (onDelete) {
            e.preventDefault();
            onDelete(selectedId);
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onUpdate(selectedId, {
            position: {
              x: Math.max(0, selectedOverlay.position.x - moveAmount),
              y: selectedOverlay.position.y,
            },
          });
          break;
        case 'ArrowRight':
          e.preventDefault();
          onUpdate(selectedId, {
            position: {
              x: Math.min(100, selectedOverlay.position.x + moveAmount),
              y: selectedOverlay.position.y,
            },
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          onUpdate(selectedId, {
            position: {
              x: selectedOverlay.position.x,
              y: Math.max(0, selectedOverlay.position.y - moveAmount),
            },
          });
          break;
        case 'ArrowDown':
          e.preventDefault();
          onUpdate(selectedId, {
            position: {
              x: selectedOverlay.position.x,
              y: Math.min(100, selectedOverlay.position.y + moveAmount),
            },
          });
          break;
        case 'Escape':
          onSelect(null);
          break;
      }
    },
    [selectedId, selectedOverlay, onUpdate, onDelete, onSelect]
  );

  // Add global event listeners
  useEffect(() => {
    if (dragState.isDragging || resizeState.isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState.isDragging, resizeState.isResizing, handleMouseMove, handleMouseUp]);

  // Keyboard event listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      ref={containerRef}
      onClick={handleBackgroundClick}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: containerWidth,
        height: containerHeight,
        cursor: dragState.isDragging ? 'grabbing' : 'default',
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      {/* Render hit targets for each overlay */}
      {overlays.map((overlay) => (
        <OverlayHitTarget
          key={overlay.id}
          overlay={overlay}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          isSelected={overlay.id === selectedId}
          onMouseDown={(e) => handleDragStart(e, overlay)}
          onClick={(e) => handleOverlayClick(e, overlay.id)}
          onResizeStart={handleResizeStart}
          scale={scale}
        />
      ))}
    </div>
  );
};

// Hit target for an individual overlay
interface OverlayHitTargetProps {
  overlay: OverlayData;
  containerWidth: number;
  containerHeight: number;
  isSelected: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
  onResizeStart: (e: React.MouseEvent, handle: ResizeHandle, measuredHeightPx: number) => void;
  scale: number;
}

const OverlayHitTarget: React.FC<OverlayHitTargetProps> = ({
  overlay,
  containerWidth,
  containerHeight,
  isSelected,
  onMouseDown,
  onClick,
  onResizeStart,
  scale,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);

  const left = (overlay.position.x / 100) * containerWidth;
  const top = (overlay.position.y / 100) * containerHeight;
  const width = (overlay.width / 100) * containerWidth;

  // For explicit height, use it. Otherwise, let content determine height.
  const hasExplicitHeight = overlay.height !== undefined;
  const height = hasExplicitHeight
    ? (overlay.height! / 100) * containerHeight
    : undefined;

  // Handle resize start by measuring actual element height
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: ResizeHandle) => {
      const measuredHeight = elementRef.current?.offsetHeight ?? 0;
      onResizeStart(e, handle, measuredHeight);
    },
    [onResizeStart]
  );

  return (
    <div
      ref={elementRef}
      onMouseDown={onMouseDown}
      onClick={onClick}
      style={{
        position: 'absolute',
        left,
        top,
        width,
        height: height, // undefined = auto-size to content
        transform: overlay.rotation ? `rotate(${overlay.rotation}deg)` : undefined,
        transformOrigin: 'top left',
        cursor: 'grab',
        // Visual feedback
        border: isSelected ? '2px solid #2196F3' : '2px solid transparent',
        boxSizing: 'border-box',
        zIndex: (overlay.zIndex ?? 0) + 1000, // Above the actual overlays
      }}
    >
      {/* Render invisible content to get accurate sizing */}
      {!hasExplicitHeight && (
        <InvisibleContent overlay={overlay} scale={scale} />
      )}

      {/* Resize handles - only show when selected */}
      {isSelected && (
        <>
          {/* Corner handles for all overlay types */}
          <ResizeHandleComponent position="top-left" onResizeStart={handleResizeStart} />
          <ResizeHandleComponent position="top-right" onResizeStart={handleResizeStart} />
          <ResizeHandleComponent position="bottom-left" onResizeStart={handleResizeStart} />
          <ResizeHandleComponent position="bottom-right" onResizeStart={handleResizeStart} />

          {/* Side handles for all overlay types */}
          <ResizeHandleComponent position="left" onResizeStart={handleResizeStart} />
          <ResizeHandleComponent position="right" onResizeStart={handleResizeStart} />

          {/* Top/bottom center handles only for non-text overlays */}
          {overlay.type !== 'text' && (
            <>
              <ResizeHandleComponent position="top" onResizeStart={handleResizeStart} />
              <ResizeHandleComponent position="bottom" onResizeStart={handleResizeStart} />
            </>
          )}
        </>
      )}
    </div>
  );
};

// Renders invisible content to determine natural height
const InvisibleContent: React.FC<{ overlay: OverlayData; scale: number }> = ({ overlay, scale }) => {
  if (overlay.type === 'text') {
    const { style } = overlay;
    // Scale padding and font size for preview
    const scaledPadding = style.padding
      ? `${(style.padding.top ?? 8) * scale}px ${(style.padding.right ?? 12) * scale}px ${(style.padding.bottom ?? 8) * scale}px ${(style.padding.left ?? 12) * scale}px`
      : `${8 * scale}px ${12 * scale}px`;

    return (
      <div
        style={{
          visibility: 'hidden',
          fontFamily: style.fontFamily,
          fontSize: style.fontSize * scale,
          fontWeight: style.fontWeight,
          fontStyle: style.fontStyle,
          lineHeight: style.lineHeight ?? 1.4,
          padding: scaledPadding,
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
        }}
      >
        {overlay.text}
      </div>
    );
  }

  // For images without height, default to square
  return <div style={{ paddingBottom: '100%' }} />;
};

// Resize handle component
interface ResizeHandleComponentProps {
  position: ResizeHandle;
  onResizeStart: (e: React.MouseEvent, handle: ResizeHandle) => void;
}

const ResizeHandleComponent: React.FC<ResizeHandleComponentProps> = React.memo(({
  position,
  onResizeStart,
}) => {
  const size = 10;
  const offset = -size / 2;

  const getPositionStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'absolute',
      width: size,
      height: size,
      backgroundColor: '#2196F3',
      border: '1px solid white',
      borderRadius: 2,
    };

    switch (position) {
      case 'top-left':
        return { ...base, top: offset, left: offset, cursor: 'nwse-resize' };
      case 'top':
        return { ...base, top: offset, left: '50%', marginLeft: offset, cursor: 'ns-resize' };
      case 'top-right':
        return { ...base, top: offset, right: offset, cursor: 'nesw-resize' };
      case 'left':
        return { ...base, top: '50%', left: offset, marginTop: offset, cursor: 'ew-resize' };
      case 'right':
        return { ...base, top: '50%', right: offset, marginTop: offset, cursor: 'ew-resize' };
      case 'bottom-left':
        return { ...base, bottom: offset, left: offset, cursor: 'nesw-resize' };
      case 'bottom':
        return { ...base, bottom: offset, left: '50%', marginLeft: offset, cursor: 'ns-resize' };
      case 'bottom-right':
        return { ...base, bottom: offset, right: offset, cursor: 'nwse-resize' };
    }
  };

  return (
    <div
      style={getPositionStyle()}
      onMouseDown={(e) => {
        e.stopPropagation();
        onResizeStart(e, position);
      }}
    />
  );
});

export default CanvasEditingLayer;
